/**
 * Tooltip 数据转换模块。
 * 专门负责根据 tooltip params 生成列定义与 records，以及旧版折线图专用结构。
 * 若需新增 tooltip 布局或转换逻辑，请在此文件维护。
 */
import { tooltipValueFormat } from './utils';
import {
  IColumn,
  ITooltipConfig,
  ITooltipParam,
  ITooltipRecord,
} from '@customComp/Chart/chartConfigs/types';

interface ITransformTooltipResult {
  columns: IColumn[];
  records: ITooltipRecord[];
}

interface IGetTooltipColumnAndRecordsConfig {
  isOnlyOneDimension: boolean;
  compareDateCount?: number;
}

const getDisplayConfig = (
  display: unknown,
): { accuracy: number; unit: string; } => {
  const accuracyRaw = (display as { accuracy?: string | number; })?.accuracy;
  const unitRaw = (display as { unit?: string; })?.unit;
  return {
    accuracy: Number(accuracyRaw ?? 0),
    unit: String(unitRaw ?? 'bmk'),
  };
};

const FIRST_COLUMN_KEY = 'first';
const COMPARE_COLUMN_KEY = 'compareDate';

export const transformTooltipParams = (
  params: ITooltipParam[],
  config: ITooltipConfig,
): ITransformTooltipResult => {
  const { isHaveCompare, metricNames, isOnlyOneDimension, fieldInfos } = config;
  const isNoCompare = isOnlyOneDimension && !isHaveCompare;
  const hasCompareCol = isHaveCompare && !isOnlyOneDimension;
  const columns: IColumn[] = [
    {
      key: FIRST_COLUMN_KEY,
      header: isNoCompare ? '' : params[0]?.axisValueLabel || '',
    },
  ];

  if (hasCompareCol) {
    columns.push({
      key: COMPARE_COLUMN_KEY,
      header: '',
    });
  }
  const displayConfig: Record<string, { accuracy: number; unit: string; }> = {};
  metricNames.forEach((metricName: string) => {
    const { accuracy, unit } =
      fieldInfos?.find((fieldInfo) => fieldInfo.name === metricName) || {
        accuracy: 0,
        unit: 'bmk',
      };
    displayConfig[metricName] = { accuracy, unit };
  });

  const metricNameSet = new Set<string>();
  const recordMap: Record<string, ITooltipRecord> = {};
  params.forEach((item: ITooltipParam) => {
    const { data, marker, axisValueLabel } = item;
    const { metric, value, isPercent, dimensions, dateText, isDecimalPercent } = data;
    metricNameSet.add(metric);
    const { accuracy, unit } = displayConfig[metric] || { accuracy: 0, unit: 'bmk' };
    const text = tooltipValueFormat(value, {
      isPercent,
      marker,
      accuracy,
      unit,
      isDecimalPercent,
    });

    const key = isNoCompare ? axisValueLabel : dimensions + dateText;

    let record: ITooltipRecord | undefined = recordMap[key];
    if (record) {
      record[metric] = text;
    } else {
      record = {
        first: isOnlyOneDimension ? (isHaveCompare ? dateText : axisValueLabel) : dimensions,
      };
      if (hasCompareCol) {
        record.compareDate = dateText;
      }
      record[metric] = text || '-';
    }
    recordMap[key] = record;
  });

  const records: ITooltipRecord[] = Object.values(recordMap);

  metricNames.forEach((name: string) => {
    if (metricNameSet.has(name)) {
      columns.push({
        key: name,
        header: name?.replace(/(t[0-2]_)/, '') || '',
      });
    }
  });

  return { columns, records };
};

export const getOneDimensionColumnAndRecords = (
  params: ITooltipParam[],
): ITransformTooltipResult => {
  const columns: IColumn[] = [
    {
      key: FIRST_COLUMN_KEY,
      header: '',
    },
  ];
  const record: Record<string, string> = {
    [FIRST_COLUMN_KEY]: params[0]?.axisValueLabel || '',
  };
  const metricNames: string[] = [];
  
  params.forEach((item: ITooltipParam) => {
    const { data, marker } = item;
    const { metricName, value, isPercent, isDecimalPercent, display } = data;
    metricNames.push(metricName);
    const { accuracy, unit } = getDisplayConfig(display);
    record[metricName] = tooltipValueFormat(value, {
      isPercent,
      isDecimalPercent,
      marker,
      accuracy,
      unit,
    });
  });

  metricNames.forEach((name: string) => {
    columns.push({
      key: name,
      header: name?.replace(/(t[0-2]_)/, '') || '',
    });
  });
  return { columns, records: [record] };
};

export const getOneDimensionWithCompareColumnAndRecords = (
  params: ITooltipParam[],
): ITransformTooltipResult => {
  const columns: IColumn[] = [
    {
      key: FIRST_COLUMN_KEY,
      header: params[0]?.axisValueLabel || '',
    },
  ];
  
  const recordMap: Map<string, ITooltipRecord> = new Map();
  const metricNameSet = new Set<string>();
  params.forEach((item: ITooltipParam) => {
    const { data, marker } = item;
    const { metricName, value, isPercent, isDecimalPercent, dateText, display } = data;
    metricNameSet.add(metricName);
    const { accuracy, unit } = getDisplayConfig(display);

    const record: ITooltipRecord = recordMap.get(dateText) ?? {};
    record[FIRST_COLUMN_KEY] = dateText;
    record[metricName] = tooltipValueFormat(value, {
      isPercent,
      marker,
      isDecimalPercent,
      accuracy,
      unit,
    });
    recordMap.set(dateText, record);
  });

  metricNameSet.forEach((name: string) => {
    columns.push({
      key: name,
      header: name?.replace(/(t[0-2]_)/, '') || '',
    });
  });
  return { columns, records: Array.from(recordMap.values()) };
};

export const getDimensionsColumnAndRecords = (
  params: ITooltipParam[],
): ITransformTooltipResult => {
  const columns: IColumn[] = [
    {
      key: FIRST_COLUMN_KEY,
      header: params[0]?.axisValueLabel || '',
    },
  ];
  const recordMap: Map<string, ITooltipRecord> = new Map();
  const metricNameSet = new Set<string>();
  params.forEach((item: ITooltipParam) => {
    const { data, marker } = item;
    const { metricName, value, isPercent, isDecimalPercent, dimensions, display } = data;
    metricNameSet.add(metricName);
    const { accuracy, unit } = getDisplayConfig(display);
    const record = recordMap.get(dimensions) ?? {};
    record[FIRST_COLUMN_KEY] = dimensions;
    record[metricName] = tooltipValueFormat(value, {
      isPercent,
      isDecimalPercent,
      marker,
      accuracy,
      unit,
    });
    recordMap.set(dimensions, record);
  });
  metricNameSet.forEach((name: string) => {
    columns.push({
      key: name,
      header: name?.replace(/(t[0-2]_)/, '') || '',
    });
  });
  return { columns, records: Array.from(recordMap.values()) };
};

export const getDimensionsWithCompareColumnAndRecords = (
  params: ITooltipParam[],
): ITransformTooltipResult => {
  const columns: IColumn[] = [
    {
      key: FIRST_COLUMN_KEY,
      header: params[0]?.axisValueLabel || '',
    },
    {
      key: COMPARE_COLUMN_KEY,
      header: '',
    },
  ];
  const recordMap: Map<string, ITooltipRecord> = new Map();
  const metricNameSet = new Set<string>();
  params.forEach((item: ITooltipParam) => {
    const { data, marker } = item;
    const { metricName, value, isPercent, isDecimalPercent, dimensions, dateText, display } = data;
    metricNameSet.add(metricName);
    const { accuracy, unit } = getDisplayConfig(display);
    const key = dimensions + dateText;
    const record: ITooltipRecord = recordMap.get(key) ?? {};
    record[FIRST_COLUMN_KEY] = dimensions;
    record[COMPARE_COLUMN_KEY] = dateText;
    record[metricName] = tooltipValueFormat(value, {
      isPercent,
      isDecimalPercent,
      marker,
      accuracy,
      unit,
    });
    recordMap.set(key, record);
  });

  metricNameSet.forEach((name: string) => {
    columns.push({
      key: name,
      header: name?.replace(/(t[0-2]_)/, '') || '',
    });
  });
  return { columns, records: Array.from(recordMap.values()) };
};

export const getTooltipColumnAndRecordsFunction = ({
  isOnlyOneDimension,
  compareDateCount,
}: IGetTooltipColumnAndRecordsConfig): ((
    params: ITooltipParam[],
  ) => ITransformTooltipResult) => {
  const hasCompare = (compareDateCount || 0) > 0;
  if (isOnlyOneDimension) {
    if (hasCompare) return getOneDimensionWithCompareColumnAndRecords;
    return getOneDimensionColumnAndRecords;
  }
  if (hasCompare) return getDimensionsWithCompareColumnAndRecords;

  return getDimensionsColumnAndRecords;
};


